<template>
    <div :class="[
        !readonly ? 'editable-cell' : '',
        (active && !loading && !error && !readonly) ? 'ec--active' : '',
        loading ? 'blurred' : '',
        error ? 'ec--error' : ''
    ]" @click.stop="enter" @keydown.esc.stop.prevent="onEsc" @keydown.stop>
        <div v-if="readonly || !active" class="editable-cell__display nowrapheader">
            <slot name="display" />
        </div>

        <div v-else class="editable-cell__editor" @focusin.capture="onFocusIn" @focusout.capture="onFocusOut">
            <slot name="edit" :deactivate="deactivate" :commit="commit" />
        </div>
    </div>
</template>

<script>
export default {
    name: "EditableCell",
    props: {
        rowId: [String, Number],
        prop: { type: String, required: true },
        readonly: { type: Boolean, default: false },

        // Provided by table
        isActive: { type: Function, required: true },   // (rowId, prop) => boolean
        onEnter: { type: Function, required: true },    // (rowId, prop) => void
        onCommit: { type: Function, required: true },   // (rowId, prop, intent) => void  <-- CHANGED
        onCancel: { type: Function, required: true },   // (rowId, prop) => void
        onNavigate: { type: Function, required: true }, // (rowId, prop, intent) => void

        // NEW: visual state from parent
        loading: { type: Boolean, default: false },
        error: { type: Boolean, default: false },
    },

    data() {
        return {
            inputEl: null,
            keyHandlerBound: null,

            hasFocusInside: false,
            pendingNav: false,
            committedViaKey: false,
            blurTimer: null,
            didInitialFocus: false,
            cancelledViaEsc: false,
        };
    },

    mounted() {
        if (this.active) this.$nextTick(this._attachKeyCapture);
    },
    updated() {
        this._detachKeyCapture();
        if (this.active) this.$nextTick(this._attachKeyCapture);
    },
    beforeDestroy() {
        clearTimeout(this.blurTimer);
        this._detachKeyCapture();
    },

    computed: {
        active() {
            try { return !!this.isActive(this.rowId, this.prop); }
            catch { return false; }
        }
    },

    methods: {
        focus() {
            this.enter();
        },
        enter() { if (!this.active) this.onEnter(this.rowId, this.prop); },
        commit(intent = null) {
            // intent can be: { kind: 'left'|'right'|'up'|'down' } or null
            this.onCommit(this.rowId, this.prop, intent);
        },
        cancel() { this.onCancel(this.rowId, this.prop); },
        deactivate() { this.onCancel(this.rowId, this.prop); },

        onEsc() { this.cancel(); },

        onFocusIn() {
            this.hasFocusInside = true;
            this.$nextTick(this._attachKeyCapture);
        },

        onFocusOut() {
            clearTimeout(this.blurTimer);
            this.blurTimer = setTimeout(() => {
                const stillInside = this.$el && this.$el.contains(document.activeElement);
                this.hasFocusInside = !!stillInside;
                if (!this.hasFocusInside) {
                    // If we just navigated via keyboard, don't double-commit on blur
                    if (this.pendingNav || this.committedViaKey) return;
                    if (this.cancelledViaEsc) {
                        this.cancelledViaEsc = false; return;
                    }
                    this.commit(null);
                }
            }, 0);
        },

        _firstFocusable() {
            const root = this.$el;
            if (!root) return null;
            const sel = [
                '.editable-cell__editor input',
                '.editable-cell__editor textarea',
                '.editable-cell__editor [contenteditable="true"]',
                '.editable-cell__editor .v-input input',
                '.editable-cell__editor .q-field__native',
                '.editable-cell__editor .el-input__inner'
            ].join(', ');
            return root.querySelector(sel);
        },

        _attachKeyCapture() {
            const el = this._firstFocusable();
            this.inputEl = el || null;
            if (!this.inputEl) return;

            if (!this.didInitialFocus) {
                try { this.inputEl.focus(); } catch {
                    // Ignore focus errors
                }
                if (typeof this.inputEl.select === 'function') {
                    try { this.inputEl.select(); } catch {
                        // Ignore select errors
                    }
                }
                this.didInitialFocus = true;
            }

            if (!this.keyHandlerBound) this.keyHandlerBound = this._keyHandler.bind(this);
            this.inputEl.addEventListener('keydown', this.keyHandlerBound, true);
        },

        _detachKeyCapture() {
            if (this.inputEl && this.keyHandlerBound) {
                this.inputEl.removeEventListener('keydown', this.keyHandlerBound, true);
            }
            this.inputEl = null;
        },

        _isDropdownOpenNear(el) {
            const doc = el && el.ownerDocument ? el.ownerDocument : document;
            const sel = [
                '.v-menu__content.menuable__content__active',
                '.v-autocomplete__content.menuable__content__active',
                '.el-select-dropdown',
                '.el-autocomplete-suggestion',
                '.q-menu', '.q-dialog',
                '[role="listbox"]',
                '[role="combobox"][aria-expanded="true"]'
            ].join(', ');
            const open = doc.querySelector(sel);
            return !!open && open.offsetParent !== null;
        },

        _caretAtEdge(el, dir) {
            if (!el) return true;
            const hasSel = typeof el.selectionStart === 'number' && typeof el.selectionEnd === 'number';
            if (!hasSel) return true;
            const { selectionStart: s, selectionEnd: e } = el;
            const len = typeof el.value === 'string' ? el.value.length : 0;
            if (dir === 'left') return s === 0 && e === 0;
            if (dir === 'right') return e === len;
            return true;
        },

        _keyHandler(e) {
            const key = e.key;


            // Let open dropdowns handle any arrow keys
            if ((key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight')
                && this._isDropdownOpenNear(e.target)) {
                return;
            }

            if (key === 'Tab' || key === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                this.committedViaKey = true;
                this.pendingNav = true; // suppress blur-commit while focus moves
                this.commit({
                    kind: key === 'Tab'
                        ? (e.shiftKey ? 'left' : 'right')
                        : (e.shiftKey ? 'up' : 'down')
                });
                // allow blur handler to resume after the focus change
                setTimeout(() => (this.pendingNav = false), 0);
                return;
            }

            if (key === 'ArrowLeft' || key === 'ArrowRight') {
                const dir = key === 'ArrowLeft' ? 'left' : 'right';
                const atEdge = this._caretAtEdge(e.target, dir);

                if (!atEdge) { e.stopPropagation(); return; } // let caret move within the input
                e.stopPropagation(); e.preventDefault();
                // Commit + navigate (same flow as Tab/Enter)
                this.committedViaKey = true;
                this.pendingNav = true;
                this.commit({ kind: dir });
                setTimeout(() => (this.pendingNav = false), 0);
                return;
            }

            if (key === 'ArrowUp' || key === 'ArrowDown') {
                e.stopPropagation(); e.preventDefault();
                // Commit + navigate (consistency with Tab/Enter)
                this.committedViaKey = true;
                this.pendingNav = true;
                this.commit({ kind: key === 'ArrowUp' ? 'up' : 'down' });
                setTimeout(() => (this.pendingNav = false), 0);
                return;
            }

            if (key === 'Escape') {
                e.stopPropagation(); e.preventDefault();
                this.onEsc();
                return;
            }

            // For all other keys, stop bubbling so scrollers don't react
            e.stopPropagation();
        },
    },

    watch: {
        active(n) {
            this._detachKeyCapture();
            if (n) {
                this.didInitialFocus = false;
                this.cancelledViaEsc = false;
                this.committedViaKey = false;
                this.$nextTick(this._attachKeyCapture);
            }
        },
    },
};
</script>

<style scoped>
.editable-cell {
    width: 100%;
    height: 100%;
    cursor: text;
    position: relative;
}

.editable-cell__display {
    min-height: 32px;
    display: flex;
    align-items: center;
}

.editable-cell__editor {
    min-height: 32px;
}

/* hover outline */
.editable-cell:not(.ec--active):hover::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border: 1px solid rgba(0, 0, 0, 0.15);
    pointer-events: none;
}

/* error outline */
.ec--error::before {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid var(--v-error-base);
    pointer-events: none;
}

/* active/editing outline */
.ec--active::before {
    content: '';
    position: absolute;
    inset: 0;
    border: 2px solid var(--v-primary-base);
    /* or a color you prefer */
    border-radius: 2px;
    pointer-events: none;
}
</style>